import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { apiClient, type ExecutionRun } from "../api/client";
import { useAuth } from "../app/AuthContext";
import { ImportProfileManager } from "./admin/ImportProfileManager";
import { AuditSearch } from "./admin/AuditSearch";
import { ProviderManager } from "./admin/ProviderManager";
import { QuotaManager } from "./admin/QuotaManager";
import { UsageHealth } from "./admin/UsageHealth";
import { errorMessage } from "./admin/errors";

export function AdminPage() {
  const { session } = useAuth();
  if (session?.role !== "manager" && session?.role !== "owner") {
    return (
      <section className="empty-page">
        <span className="eyebrow">权限不足</span>
        <h1>平台管理</h1>
        <p>此页面只对完成 MFA 的 manager 和 owner 开放。</p>
      </section>
    );
  }

  return (
    <div className="page-stack admin-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">范围化治理</span>
          <h1>平台管理</h1>
          <p>所有查询与变更均按当前角色、部门和资源授权重新校验，并写入只追加审计。</p>
        </div>
        <span className="phase-badge">{session.role}</span>
      </header>

      <nav aria-label="管理模块" className="admin-shortcuts">
        <Link to="/users">用户管理</Link>
        <a href="#profiles">导入模板</a>
        <a href="#quotas">配额</a>
        {session.role === "owner" && <a href="#providers">供应商与全局策略</a>}
        {session.role === "owner" && <a href="#usage-health">用量与健康</a>}
        {session.role === "owner" && <a href="#audit">审计检索</a>}
        <Link to="/replenishment">补货策略</Link>
        <Link to="/runs">团队运行</Link>
      </nav>

      <ImportProfileManager />
      <QuotaManager />
      {session.role === "owner" && <ProviderManager />}
      {session.role === "owner" && <UsageHealth />}
      {session.role === "owner" && <AuditSearch />}
      <TeamRuns currentUserId={session.user_id} />

      <section className="admin-link-panel flow-panel">
        <div>
          <span className="eyebrow">版本化业务规则</span>
          <h2>补货策略管理</h2>
          <p>在补货建议页面创建草稿、追加版本并发布；历史运行继续固定引用旧版本。</p>
        </div>
        <Link className="secondary-button" to="/replenishment">
          打开策略管理
        </Link>
      </section>
    </div>
  );
}

function TeamRuns({ currentUserId }: { currentUserId: string }) {
  const [runs, setRuns] = useState<ExecutionRun[]>([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    apiClient
      .listExecutionRuns()
      .then(setRuns)
      .catch((error: unknown) => setMessage(errorMessage(error)));
  }, []);

  const teamRuns = runs.filter((run) => run.requested_by_user_id !== currentUserId);
  return (
    <section className="flow-panel" id="team-runs">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">当前部门与资源双重范围</span>
          <h2>团队运行</h2>
        </div>
        <Link className="secondary-button" to="/runs">
          查看运行中心
        </Link>
      </div>
      {message && (
        <p className="alert error" role="alert">
          {message}
        </p>
      )}
      <div className="admin-run-list">
        {teamRuns.slice(0, 6).map((run) => (
          <Link key={run.id} to={`/runs/${run.id}`}>
            <strong>{run.target}</strong>
            <span>
              {run.state} · 发起人 {run.requested_by_user_id.slice(0, 8)}
            </span>
          </Link>
        ))}
        {!teamRuns.length && <p className="panel-copy">暂无当前授权范围内的团队运行。</p>}
      </div>
    </section>
  );
}
